import { CommerceService } from "@/services/commerce"
import { AppSettings } from "@/settings"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getArticlesFromSkuArray } from "../../shared/utils/getArticles"
import { getCustomer } from "../../shared/utils/getCustomer"
import { removeCustomLineItemAction } from "../utils/cartHelpers"
import { getActiveCustomerCart } from "../utils/getCart"

const commerceApi = CommerceService.api()

export const mutationSyncCustomerCartWithStore = {
  async syncCustomerCartWithStore(_parent, args, context, _info) {
    const { branchId, customerNumber, cdn, lang, ignoreReservation } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.CARTS,
      operation: Operations.UPDATE,
    })

    const customer = await getCustomer({ number: customerNumber })

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customer.customerNumber,
    })

    const customerCarts = await getActiveCustomerCart(customer.id, user.employeeNumber)
    const customerCart = customerCarts[0]

    if (isValidCart(customerCart)) {
      return null
    }

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })

    const { customLineItems, store } = customerCart

    // form an array of skus for query of articles
    let skuArray = customLineItems.map((item) => item?.custom?.fields?.sku || null)
    skuArray = skuArray.filter((n) => n)

    const articles = await getArticlesFromSkuArray(client, skuArray)
    const products = await productActiveInCt(skuArray)

    // nothing to do, something else was wrong
    if (!articles || articles.length === 0) {
      throw new Error("No articles found")
    }

    let actions = setCustomLineItemRemovalAction({
      customLineItems,
      articles,
      branchId,
      products,
    })

    if (!ignoreReservation && customerCart?.custom?.fields?.reservationNumber) {
      actions.push(getReservationActions(customerCart?.custom?.type?.id, customerCart?.custom?.fields))
    }

    if (!ignoreReservation && customerCart?.custom?.fields?.picklistNumber) {
      actions.push(getPicklistActions(customerCart?.custom?.type?.id, customerCart?.custom?.fields))
    }

    if (actions.length === 0) {
      return customerCart
    }

    const updatedCart = await updateCustomerCart({ customerCart, actions, store, client, branchId })

    return updatedCart
  },
}

/**
 * Checks if cart contains all required values
 *
 * @param {object} customerCart
 *
 * @returns {boolean}
 */
const isValidCart = (customerCart) =>
  customerCart?.length === 0 || !customerCart?.customLineItems || customerCart?.customLineItems.length === 0

const targetInvalid = (targetArticle) =>
  !targetArticle?.notAvailableInBranchOffices || targetArticle?.notAvailableInBranchOffices?.length === 0

/**
 * Returs the removal actions that will be passed to the payload
 *
 * @param {{ customLineItems: array, articles: array, branchId: string, products: array }}
 *
 * @returns {{action: "removeCustomLineItem", customLineItemId: string}[]}
 */
function setCustomLineItemRemovalAction({ customLineItems, articles, branchId, products }) {
  let actions = customLineItems.map((item) => {
    // find target article containing notAvailableInBranchOffices which will be used for the check
    const targetArticle = articles?.find((article) => article?.sku === item?.custom?.fields?.sku)

    // target article is not in sanity and therefore should be removed
    if (!targetArticle) {
      return removeCustomLineItemAction(item?.id)
    }

    const targetProduct = products?.find((product) => product?.key === `orphan-article-${item?.custom?.fields?.sku}`)

    if (!targetProduct || !targetProduct?.masterData?.published) {
      return removeCustomLineItemAction(item?.id)
    }

    // no branch offices information and in general commercetools shows that this is published
    if (targetInvalid(targetArticle)) {
      return null
    }

    const articleNotAvailableInBranch = targetArticle?.notAvailableInBranchOffices?.find(
      (branch) => branch?.id === branchId,
    )

    // was not found, nothing to do, article is not blocked
    if (!articleNotAvailableInBranch) {
      return null
    }

    return removeCustomLineItemAction(item?.id)
  })

  actions = actions.filter((n) => n)

  return actions
}

/**
 * Updates the customer cart or throws the error
 *
 * @param {{customerCart: object, actions: array, store: object}}
 *
 * @returns {object}
 * @throws {Error}
 */
async function updateCustomerCart({ customerCart, actions, store }) {
  const payload = {
    version: customerCart.version,
    actions,
    ...(store && { storeKey: store.key }),
  }

  try {
    let updatedCart = (
      await commerceApi
        .carts()
        .withId({ ID: customerCart.id })
        .post({
          body: JSON.stringify(payload),
        })
        .execute()
    )?.body

    return updatedCart
  } catch (err) {
    throw new Error(err)
  }
}

/**
 * Retrieves products from commercetools by given skus
 *
 * @param {array} skuArray
 * @returns {Promise<array>}
 */
async function productActiveInCt(skuArray) {
  const prudctKeys = skuArray.map((sku) => `"orphan-article-${sku}"`).join(",")
  const products = (
    await commerceApi
      .products()
      .get({ queryArgs: { where: `key in (${prudctKeys})`, limit: 200 } })
      .execute()
  )?.body?.results

  return products || []
}

/**
 * @param {string} customTypeCartId
 * @param {object} customCartFields
 * @returns {object|null}
 */
const getReservationActions = (customTypeCartId, customCartFields) => {
  if (!customTypeCartId) return null

  /** @type {string} */
  const createdByEmployeeNumber = customCartFields.createdByEmployeeNumber
  const freightSurcharge = customCartFields?.freightSurcharge
  const reservationNumber = customCartFields?.reservationNumber

  return {
    action: "setCustomType",
    type: {
      typeId: "type",
      id: customTypeCartId,
    },
    fields: {
      ...(freightSurcharge && { freightSurcharge }),
      createdByEmployeeNumber,
      ...(reservationNumber && { reservationNumber }),
    },
  }
}

/**
 * @param {string} customTypeCartId
 * @param {object} customCartFields
 * @returns {object|null}
 */
const getPicklistActions = (customTypeCartId, customCartFields) => {
  if (!customTypeCartId) return null

  /** @type {string} */
  const createdByEmployeeNumber = customCartFields.createdByEmployeeNumber
  const freightSurcharge = customCartFields?.freightSurcharge
  const picklistNumber = customCartFields?.picklistNumber

  return {
    action: "setCustomType",
    type: {
      typeId: "type",
      id: customTypeCartId,
    },
    fields: {
      ...(freightSurcharge && { freightSurcharge }),
      createdByEmployeeNumber,
      ...(picklistNumber && { picklistNumber }),
    },
  }
}
